/**
 * relatorioPermissionMixin
 *
 * Mixin para controle de acesso em telas de relatório.
 *
 * Como usar numa view de relatório:
 *   import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";
 *   export default {
 *     mixins: [relatorioPermissionMixin],
 *     data() {
 *       return {
 *         // Schemas OBRIGATÓRIOS para ver este relatório (qualquer um é suficiente)
 *         relatorioSchemas: ["Project"],
 *         // Se true, filtra dados pelo owner (para schemas com owner_schema: true)
 *         relatorioFiltrarPorOwner: false,
 *         ...
 *       };
 *     }
 *   };
 *
 * Propriedades expostas:
 *   - relatorioPermissoes     → schemaPermissions carregadas
 *   - relatorioUsuarioId      → userId do usuário logado
 *   - relatorioOwnerFiltro    → [userId] para usar em queries de schemas com owner_schema
 *   - relatorioAcessoVerificado → true após carregamento inicial
 *
 * Métodos expostos:
 *   - relatorioTemPermissao(schemas)  → boolean
 *   - relatorioRedirecionar()         → redireciona para /relatorios se sem acesso
 */

import authService from "@/services/authService";
import { getSeparateActions } from "@/services/actionService";

export default {
  data() {
    return {
      relatorioPermissoes: [],
      relatorioActionsPermitidas: [],
      relatorioActionsConfiguradas: [],
      relatorioControlePorActionAtivo: false,
      relatorioUsuarioId: null,
      relatorioAcessoVerificado: false,
      // As views devem declarar esses campos
      relatorioSchemas: [],
      relatorioFiltrarPorOwner: false
    };
  },

  computed: {
    relatorioOwnerFiltro() {
      if (!this.relatorioFiltrarPorOwner) return null;
      return this.relatorioUsuarioId ? [this.relatorioUsuarioId] : null;
    }
  },

  methods: {
    _relatorioNormalizarRota(rota = "") {
      let valor = String(rota || "").trim();
      if (!valor) return "";
      if (valor.startsWith("#/")) valor = valor.slice(1);
      if (!valor.startsWith("/")) valor = `/${valor}`;

      const [semQuery] = valor.split("?");
      const [path, hash] = semQuery.split("#");
      const pathNormalizado = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
      return hash ? `${pathNormalizado}#${String(hash).toLowerCase()}` : pathNormalizado;
    },

    _relatorioExtrairRotaAction(javascriptCode = "") {
      const codigo = String(javascriptCode || "");
      const match = codigo.match(/route\s*:\s*["']([^"']+)["']/i);
      return match?.[1] || null;
    },

    relatorioTemPermissao(_schemas = null, rota = null) {
      const rotaAtual = this._relatorioNormalizarRota(rota || this.$route?.path || "");
      const rotaAtualSemHash = rotaAtual.split("#")[0];

      if (rotaAtualSemHash?.startsWith("/relatorios")) {
        if (!this.relatorioControlePorActionAtivo) return false;
        if (!(this.relatorioActionsConfiguradas || []).includes(rotaAtual)) return false;
        return (this.relatorioActionsPermitidas || []).includes(rotaAtual);
      }

      return true;
    },

    relatorioRedirecionar() {
      this.$notfy?.warning("Você não tem permissão para acessar este relatório.");
      if (this.$router) {
        const rotaAtual = this._relatorioNormalizarRota(this.$route?.fullPath || this.$route?.path || "");
        const hash = rotaAtual.includes("#comercial") || rotaAtual.includes("/comercial/")
          ? "#comercial"
          : "#financeiro";
        this.$router.push({ path: "/relatorios", hash });
      }
    },

    async _relatorioCarregarPermissoes() {
      try {
        const params = await authService.getUserParams();
        this.relatorioUsuarioId = params?.userId || params?.id || null;

        const userId = this.relatorioUsuarioId;
        if (!userId || !this.$acessGroup) return;

        const response = await this.$acessGroup.findPermissionsByUserId(userId);
        const schemas = response?.data?.schemas || response?.schemas || [];

        this.relatorioPermissoes = Array.isArray(schemas)
          ? schemas.map((x) => ({
              schemaName: x.schemaName,
              permissions: Array.isArray(x.permissions) ? x.permissions : []
            }))
          : [];

        const actionIdsPermitidos = (Array.isArray(schemas) ? schemas : [])
          .flatMap((schema) => (Array.isArray(schema?.actions) ? schema.actions : []))
          .map((action) => action?.actionId)
          .filter(Boolean);

        const actionsSeparadas = await getSeparateActions();
        const rotasRelatoriosConfiguradas = (Array.isArray(actionsSeparadas) ? actionsSeparadas : [])
          .map((action) => this._relatorioExtrairRotaAction(action?.javascriptCode))
          .map((rota) => this._relatorioNormalizarRota(rota))
          .filter((rota) => rota?.startsWith("/relatorios"));

        this.relatorioActionsConfiguradas = [...new Set(rotasRelatoriosConfiguradas)];
        this.relatorioControlePorActionAtivo = this.relatorioActionsConfiguradas.length > 0;

        this.relatorioActionsPermitidas = (Array.isArray(actionsSeparadas) ? actionsSeparadas : [])
          .filter((action) => actionIdsPermitidos.includes(action?.id))
          .map((action) => this._relatorioExtrairRotaAction(action?.javascriptCode))
          .map((rota) => this._relatorioNormalizarRota(rota))
          .filter((rota) => rota?.startsWith("/relatorios"));

        this.relatorioActionsPermitidas = [...new Set(this.relatorioActionsPermitidas)];
      } catch (_) {
        // Em caso de erro, bloqueia acesso de relatorios para nao vazar permissao.
        this.relatorioPermissoes = [];
        this.relatorioActionsConfiguradas = [];
        this.relatorioActionsPermitidas = [];
        this.relatorioControlePorActionAtivo = false;
      } finally {
        this.relatorioAcessoVerificado = true;
      }
    }
  },

  async mounted() {
    await this._relatorioCarregarPermissoes();

    if (!this.relatorioTemPermissao(null, this.$route?.fullPath || this.$route?.path)) {
      this.relatorioRedirecionar();
    }
  }
};
